<template>
  <v-card>
    <v-card-title
      class="primary white--text py-1 my-0 title-sticky d-print-none"
    >
      {{ title }}
      <v-spacer></v-spacer>
      <v-btn
        v-if="hasApprovalPanel"
        small
        tile
        color="secondary"
        class="mx-1 approval-toggle"
        :aria-expanded="String(approvalOpen)"
        :aria-controls="approvalPanelId"
        @click="toggleApprovalPanel"
      >
        <v-badge
          dot
          overlap
          color="warning"
          :value="approvalPending"
          class="mr-2"
        >
          <v-icon small>mdi-timeline-text-outline</v-icon>
        </v-badge>
        <span class="approval-toggle__label">
          {{ approvalOpen ? '收起审批' : '审批流程' }}
        </span>
      </v-btn>
      <slot name="titlebtns">
        <v-btn
          @click="closeAndTo(backRouteName, {}, {})"
          color="secondary"
          small
          tile
          class="mx-1"
        >
          <v-icon left dark>mdi-format-list-bulleted-square</v-icon>
          返回列表
        </v-btn>
        <v-btn
          v-if="canSave && !hideBtn"
          width="80"
          tile
          @click="save"
          color="success"
          small
          class="mx-1"
          :loading="loading"
        >
          保存为草稿
        </v-btn>
        <v-btn
          v-if="canSubmit"
          width="80"
          tile
          @click="submit"
          color="success"
          small
          class="mx-1"
          :loading="sloading"
        >
          保存并提交
        </v-btn>
        <v-btn
          v-if="canPrint"
          width="60"
          tile
          @click="printElement(title)"
          color="primary"
          small
          class="mx-1"
        >
          打印
        </v-btn>
        <v-btn
          v-if="canSaveFile"
          tile
          color="warning"
          small
          class="mx-1"
          @click="saveFile"
          :loading="quoteLoading"
          v-permission="['费用凭证:保存附件']"
        >
          <span class="mdi mdi-file-arrow-up-down-outline"></span>
          保存附件
        </v-btn>
        <v-btn
          v-if="canConfirm"
          tile
          color="error"
          small
          class="mx-1"
          @click="confirmAction"
          :loading="quoteLoading"
          v-permission="['费用凭证:实际申请人确认']"
        >
          <v-icon left>mdi-check</v-icon>
          实际申请人确认
        </v-btn>
        <v-btn
          v-if="canConfirm"
          tile
          color="error"
          small
          class="mx-1"
          @click="returnAction"
          :loading="quoteLoading"
          v-permission="['费用凭证:退回']"
        >
          <v-icon left>mdi-check</v-icon>
          退回
        </v-btn>
        <slot name="custombtns"></slot>
      </slot>
    </v-card-title>
    <div
      class="detail-view-body"
      :class="{
        'detail-view-body--approval-ready': hasApprovalPanel,
        'detail-view-body--with-approval': hasApprovalPanel && approvalOpen,
      }"
      @keydown.esc="closeApprovalPanel"
    >
      <main class="detail-view-main">
        <slot name="main-topcontent"></slot>
        <div v-for="s in subtitles" :key="s">
          <v-card-title class="h6 mt-1 ml-1 left-border">
            {{ s }}
            <v-spacer></v-spacer>
            <slot :name="`${s}按钮`"></slot>
          </v-card-title>
          <slot :name="s"></slot>
          <v-divider></v-divider>
        </div>
        <slot></slot>
      </main>
      <transition name="approval-panel">
        <aside
          v-show="hasApprovalPanel && approvalOpen"
          :id="approvalPanelId"
          ref="approvalAside"
          class="approval-aside"
          tabindex="-1"
          role="complementary"
          aria-label="审批流程"
        >
          <div class="approval-aside__toolbar d-print-none">
            <div class="approval-aside__heading">
              <v-icon color="primary" class="mr-2">
                mdi-timeline-check-outline
              </v-icon>
              <span>审批流程</span>
              <v-chip
                x-small
                class="ml-2"
                :color="approvalPending ? 'warning' : 'secondary'"
                :outlined="!approvalPending"
              >
                {{ approvalPending ? '待审批' : '流程记录' }}
              </v-chip>
            </div>
            <v-btn icon small aria-label="收起审批" @click="closeApprovalPanel">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
          <div
            class="approval-aside__content"
            tabindex="0"
            role="region"
            aria-label="审批内容"
          >
            <slot name="topcontent"></slot>
          </div>
        </aside>
      </transition>
      <transition name="approval-backdrop">
        <div
          v-show="hasApprovalPanel && approvalOpen"
          class="approval-backdrop d-print-none"
          aria-hidden="true"
          @click="closeApprovalPanel"
        ></div>
      </transition>
    </div>
    <v-dialog
      attach="#mask"
      hide-overlay
      width="600"
      persistent
      v-model="confirmOpen"
    >
      <v-card>
        <v-card-title>
          请输入确认说明
          <v-spacer></v-spacer>
          <v-btn
            small
            outlined
            tile
            class="mx-1"
            @click="confirmSubmit"
            color="success"
          >
            <v-icon left>mdi-check</v-icon>
            确认
          </v-btn>
          <v-btn small outlined tile class="mx-1" @click="confirmOpen = false">
            <v-icon>mdi-close</v-icon>
            关闭
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form ref="confirmForm">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    outlined
                    dense
                    v-model="confirmRemark"
                    label="确认说明"
                    :rules="[rules.required]"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      attach="#mask"
      hide-overlay
      width="600"
      persistent
      v-model="returnOpen"
    >
      <v-card>
        <v-card-title>
          请输入退回原因
          <v-spacer></v-spacer>
          <v-btn
            small
            outlined
            tile
            class="mx-1"
            @click="returnSubmit"
            color="success"
          >
            <v-icon left>mdi-check</v-icon>
            确认
          </v-btn>
          <v-btn small outlined tile class="mx-1" @click="returnOpen = false">
            <v-icon>mdi-close</v-icon>
            关闭
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-form ref="returnForm">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    outlined
                    dense
                    v-model.trim="returnRemark"
                    label="退回原因"
                    :rules="[rules.requiredTrim]"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <iframe
      ref="printFrame"
      style="visibility: hidden; position: absolute"
    ></iframe>
  </v-card>
</template>
<script>
import routerControl from '@/mixin/routerControl'
import print from '@/mixin/print'
import approvalPanel from '@/mixin/approvalPanel'
export default {
  name: 'v-detail-view-invoice',
  mixins: [routerControl, print, approvalPanel],
  props: {
    title: String,
    backRouteName: String,
    subtitles: Array,
    tooltip: String,
    canSubmit: [Boolean, String],
    canSave: {
      type: Boolean,
      default: true,
    },
    canPrint: [Boolean, String],
    canSaveFile: [Boolean, String],
    canConfirm: [Boolean, String],
    detail: {
      type: Object,
    },
  },
  data() {
    return {
      hideBtn: false,
      loading: false,
      sloading: false,
      quoteLoading: false,
      confirmOpen: false,
      confirmRemark: '',
      returnOpen: false,
      returnRemark: '',
      rules: {
        required: (v) => !!v || v === 0 || '必填项不能为空',
        requiredTrim: (v) => !!String(v ?? '').trim() || '必填项不能为空',
      },
    }
  },

  watch: {
    tooltip(tooltip) {
      this.$store.commit('updateViewTagsTooltip', {
        tooltip,
        nowFullPath: this.$route.path,
      })
    },
  },

  methods: {
    async emitPromise(method, ...params) {
      let listener =
        this.$listeners[method] || this.$attrs[method] || this[method]
      if (listener) {
        //one can additionally wrap this in try/catch if needed and handle the error further
        let res = await listener(...params)
        return res === undefined || res
      }
      return false
    },
    async save() {
      this.loading = true
      try {
        await this.emitPromise('save', () => {
          this.closeAndTo(this.backRouteName)
        })
      } finally {
        this.loading = false
      }
    },
    async submit() {
      // console.log('aaaaaaaaaaaaaaaaaaaaaaaaaa，', this.detail)
      this.sloading = true
      try {
        await this.emitPromise('submit', () => {
          this.closeAndTo(this.backRouteName)
        })
      } finally {
        this.sloading = false
      }
    },
    updateTooltip(tooltip) {
      this.$store.commit('updateViewTagsTooltip', {
        tooltip,
        nowFullPath: this.$route.path,
      })
    },
    async saveFile() {
      console.log('aaaaaaaaaaaaaaaaaaaaaaaaaa，', this.detail)
      const reqUrl = '/business/shipAffairs/costOrder/modifyCostOrderFile'
      const { errorRaw } = await this.postAsync(reqUrl, {
        ...this.detail,
      })
      if (!errorRaw) {
        this.$dialog.message.success(`附件保存成功`)
        this.closeAndTo(this.backRouteName)
      }
    },
    async startProcess(confirmRemark) {
      this.quoteLoading = true
      const { errorRaw } = await this.getAsync(
        '/business/shipAffairs/costOrder/process/start',
        { id: this.detail.id, confirmRemark },
      )
      this.quoteLoading = false
      if (!errorRaw) this.closeAndTo(this.backRouteName)
    },
    async confirmAction() {
      const handled = await this.emitPromise('confirm')
      if (!handled) {
        this.confirmRemark = ''
        this.confirmOpen = true
      }
    },
    async confirmSubmit() {
      if (!(this.$refs?.confirmForm?.validate() ?? true)) return
      this.confirmOpen = false
      await this.startProcess(this.confirmRemark)
    },
    returnAction() {
      this.returnRemark = ''
      this.returnOpen = true
    },
    async returnSubmit() {
      if (!(this.$refs?.returnForm?.validate() ?? true)) return
      this.returnOpen = false
      await this.returnProcess(this.returnRemark)
    },
    getNowText() {
      const now = new Date()
      const pad = (num) => String(num).padStart(2, '0')
      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
        now.getDate(),
      )} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
        now.getSeconds(),
      )}`
    },
    async returnProcess() {
      console.log('aaaaaaaaaaaaaaaaaaaaaaaaaa，', this.detail)
      const returnRemark = String(this.returnRemark ?? '').trim()
      if (!returnRemark) {
        this.$dialog.message.error('退回原因不能为空')
        return false
      }
      this.quoteLoading = true
      // this.detail.status = 10
      // this.detail.businessStatus = '未提交'
      // const idList = this.projects.map((item) => item.id)
      const reqUrl = '/business/shipAffairs/costOrder/modifyCostOrder'
      const remarkText = `实际申请人退回原因(${this.getNowText()})：${returnRemark}`
      const remark = this.detail.remark
        ? `${this.detail.remark}\n${remarkText}`
        : remarkText
      const { errorRaw } = await this.postAsync(reqUrl, {
        id: this.detail.id,
        status: 10,
        businessStatus: '实际申请人退回',
        remark,
        // idList,
      })
      if (errorRaw) {
        this.quoteLoading = false
        return false
      }
      this.quoteLoading = false
      if (!errorRaw) this.closeAndTo(this.backRouteName)
    },
  },
  created() {
    this.updateTooltip(this.tooltip)
  },
}
</script>

<style>
.left-border {
  position: relative;
}
.left-border::before {
  content: '';
  width: 5px;
  height: 30px;
  position: absolute;
  top: 25%;
  left: 0;
  background: #3399cc;
}
.title-sticky {
  position: sticky;
  top: 85px;
  z-index: 3;
}
</style>
